/**
 * Stale-bundle recovery.
 *
 * When a deploy ships, the client's hashed assets (e.g. `index-BE1cLMnX.js`) are
 * replaced. A user whose tab was open *before* the deploy still references the old
 * chunk URLs; the moment they navigate to a lazy route, the dynamic `import()`
 * 404s and React Router renders a scary "Something went wrong — Failed to fetch
 * dynamically imported module" screen.
 *
 * Vite emits a `vite:preloadError` window event when its preload helper (used for
 * every dynamic import in the production build) fails. We listen for it and reload
 * the page once to pull in the fresh bundle — turning a dead-end error into a
 * seamless refresh.
 *
 * Guarded against reload loops: if we already reloaded within RELOAD_GUARD_MS
 * (i.e. the asset is *genuinely* unreachable — server down, not just redeployed),
 * we do NOT reload again. We let the error surface so the user sees the real
 * failure instead of an infinite refresh.
 */
const RELOAD_GUARD_KEY = "preload-error-reloaded-at";
const RELOAD_GUARD_MS = 10_000;

type PreloadErrorDeps = {
  now: () => number;
  storage: Pick<Storage, "getItem" | "setItem">;
  reload: () => void;
};

/**
 * Decide whether to reload in response to a failed chunk preload. Returns true if
 * it reloaded. Pure except for the injected effects — unit-testable.
 */
export const handlePreloadError = (event: Event, deps: PreloadErrorDeps): boolean => {
  const last = Number(deps.storage.getItem(RELOAD_GUARD_KEY) ?? 0);
  if (deps.now() - last < RELOAD_GUARD_MS) {
    // We already reloaded moments ago and the chunk STILL won't load — this is a
    // real outage, not a redeploy. Don't loop; let the error boundary show it.
    return false;
  }
  event.preventDefault(); // suppress Vite's default re-throw — we're handling it
  deps.storage.setItem(RELOAD_GUARD_KEY, String(deps.now()));
  deps.reload();
  return true;
};

/** Wire the `vite:preloadError` listener. Call once, at app startup. */
export const registerPreloadErrorReload = (): void => {
  if (typeof window === "undefined") return;
  window.addEventListener("vite:preloadError", (event) => {
    handlePreloadError(event, {
      now: () => Date.now(),
      storage: window.sessionStorage,
      reload: () => {
        window.location.reload();
      },
    });
  });
};
